import { Typography, Stack, Badge } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { TableHeaderType, FilterType } from "@/components/tables/types";
import { Paginate } from "@/components/paginate";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useSSOAuditEvents } from "./hooks";
import { EVENT_TYPES, type SSOAuditEventResponse } from "./types";

const DEFAULT_PAGE_SIZE = 20;

type AuditRow = SSOAuditEventResponse & { id: number };

const formatDate = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

const getEventTypeLabel = (value: string) =>
  EVENT_TYPES.find(t => t.value === value)?.label ?? value;

const eventFilterOptions = EVENT_TYPES.map(et => ({
  label: et.label,
  value: et.value,
}));

const formatEntityType = (type: string | null | undefined) => {
  if (!type) return null;
  const labels: Record<string, string> = { team: "Team", role: "Role", user: "User" };
  return labels[type] ?? type;
};

const formatSource = (item: AuditRow) => {
  if (item.source === "sso_sync") return "SSO Sync";
  if (item.performed_by_email) return item.performed_by_email;
  if (item.source === "admin_api") return "Admin API";
  if (item.source === "admin_ui") return "Admin UI";
  return item.source;
};

export const AuditHistory = () => {
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();

  const eventTypeFilter = searchParams.get("event_type") ?? "";
  const page = parseInt(searchParams.get("page") ?? "0", 10);
  const pageSize = parseInt(searchParams.get("pageSize") ?? String(DEFAULT_PAGE_SIZE), 10);

  const { data, isLoading } = useSSOAuditEvents({
    event_type: eventTypeFilter || undefined,
    limit: pageSize,
    offset: page * pageSize,
  });

  const events = data?.events ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  const header: TableHeaderType<AuditRow>[] = [
    {
      id: 1,
      label: "Time",
      uid: "created_at",
      width: "13%",
      render: item => (
        <span className="al-text-xs al-text-gray-500 al-whitespace-nowrap">
          {formatDate(item.created_at)}
        </span>
      ),
    },
    {
      id: 2,
      label: "User",
      uid: "user_email",
      width: "15%",
      render: item => (
        <span className="al-text-xs al-truncate al-block" title={item.user_email ?? undefined}>
          {item.user_email ?? "\u2014"}
        </span>
      ),
    },
    {
      id: 3,
      label: "Event",
      uid: "event_type",
      width: "12%",
      filter: {
        filterType: FilterType.DROPDOWN_SINGLE,
        searchKey: "event_type",
        options: eventFilterOptions,
        value: eventTypeFilter,
        onChange: (val: string) => {
          updateSearchParamsByKey({
            event_type: val || undefined,
            page: "0",
          });
        },
      },
      render: item => <Badge variant="secondary">{getEventTypeLabel(item.event_type)}</Badge>,
    },
    {
      id: 4,
      label: "Entity Type",
      uid: "target_type",
      width: "8%",
      render: item => {
        const label = formatEntityType(item.target_type);
        return label ? (
          <Badge variant="outline" className="al-text-xs">
            {label}
          </Badge>
        ) : (
          <span className="al-text-xs al-text-gray-400">{"\u2014"}</span>
        );
      },
    },
    {
      id: 5,
      label: "Target",
      uid: "target_name",
      width: "12%",
      render: item => (
        <span className="al-text-xs al-truncate al-block" title={item.target_name ?? undefined}>
          {item.target_name ??
            (item.target_type ? `${item.target_type}:${item.target_id}` : "\u2014")}
        </span>
      ),
    },
    {
      id: 6,
      label: "Old Value",
      uid: "old_value",
      width: "12%",
      render: item => (
        <span
          className="al-text-xs al-text-gray-500 al-truncate al-block"
          title={item.old_value ?? undefined}
        >
          {item.old_value ?? "\u2014"}
        </span>
      ),
    },
    {
      id: 7,
      label: "New Value",
      uid: "new_value",
      width: "12%",
      render: item => (
        <span
          className="al-text-xs al-text-gray-500 al-truncate al-block"
          title={item.new_value ?? undefined}
        >
          {item.new_value ?? "\u2014"}
        </span>
      ),
    },
    {
      id: 8,
      label: "Source",
      uid: "source",
      width: "16%",
      render: item => (
        <span
          className="al-text-xs al-text-gray-400 al-truncate al-block"
          title={formatSource(item)}
        >
          {formatSource(item)}
        </span>
      ),
    },
  ];

  return (
    <div className="al-space-y-4">
      <Stack direction="row" className="al-items-center al-gap-2">
        <Typography variant="body" className="al-text-gray-500 al-text-sm">
          {total} event{total !== 1 ? "s" : ""}
        </Typography>
      </Stack>

      <DataTable<AuditRow>
        header={header}
        data={{ items: events as AuditRow[] }}
        idKey={item => String(item.id)}
        isLoading={isLoading}
      />

      <Paginate
        itemCount={total}
        page={page}
        numPages={totalPages}
        onPageClick={v => updateSearchParamsByKey({ page: String(v) })}
        pageSize={pageSize}
        onPageSizeChange={v => updateSearchParamsByKey({ pageSize: String(v), page: "0" })}
        showPageSize
        isLoading={isLoading}
      />
    </div>
  );
};
